<template>
	<div class="referral-container">
		<div class="title-container">
			<div class="centered" v-html="svgStore.get('iconPresent')"></div>
			<p>{{ getString('referral_claim') }}</p>
		</div>
		<AppCard no-header inset class="mb-30 card-background">
			<template #body>
				<AppCard no-header style="max-width: 270px">
					<template #body>
						<div class="col-12 mb-10">
							<AppCard no-header inset>
								<template #body>
									<p class="referral-earnings-caption">{{ getString('available_credit') }}</p>
									<p class="referral-earnings-value">{{ getPriceFormatted(userStore?.normalizedUser?.credit || 0, '€') }}</p>
								</template>
							</AppCard>
						</div>
						<div class="col-12">
							<AppButton @click="referredBottomSheetActive = true" style="width: 100%" class="mb-10" :text="getString('invited_friends')" outline />
							<AppButton @click="rewardsManagerActive = true" style="width: 100%" :text="getString('manage_rewards')" />
						</div>
					</template>
				</AppCard>
			</template>
		</AppCard>
		<p class="second-title mb-10">{{ getString('how_does_it_work') }}</p>
		<AppList class="instructions-list mb-30" :items="instructionsListItems">
			<template v-slot:instruction="{item}">
				<div class="row">
					<div class="icon-container" v-html="svgStore.get(item.icon)"></div>
					<div class="text-container">
						<div class="list-item-header">{{ item.title }}</div>
						<div class="extra-text">{{item.subtitle}}</div>
					</div>
				</div>
			</template>
		</AppList>
		<p class="second-title mb-10">{{ getString('invite_friend') }}</p>
		<div class="col-12 mb-20" v-if="referralLinkToShare && (sharedFirstTime || errorShareLink)">
			<AppTextInput readonly :label="getString('your_referral_link')" :value="referralLinkToShare" ref="referral-link-fallback-input" />
		</div>
		<div class="row">
			<AppButton @click="clickShareReferral" :loading="loadingShareLink" :text="getString(sharedFirstTime ? 'copy' : 'share')" :disabled="referralLinkToShare && errorShareLink" />
			<AppButton @click="clickEmailReferralInvite" :loading="loadingSendEmail" class="ml-10" :text="getString('email')" outline />
		</div>
		<ReferredUsersTable :active="referredBottomSheetActive" @toggling-popup="v => referredBottomSheetActive = v" />
		<RewardsManager :active="rewardsManagerActive" @toggling-popup="v => rewardsManagerActive = v" />
		<AppPopup
			:title="getString('invite_friend')"
			:body-text="getString('confirm_send_referral_email_text')"
			:buttons="popupButtons"
			:prevent-overlay-dismiss="loadingSendEmail"
			ref="confirm-send-email"
		>
			<AppTextInput ref="inviting-email-input" :readonly="loadingSendEmail" @pressed-enter="confirmEmailReferralInvite" @changed-text="v=>invitingEmail=v" :label="getString('email')" class="mb-20" />
		</AppPopup>
		<AppNotification :title="getString('success')" :body-text="getString('share_link_copy_success')" :type="1" ref="success-share-copy" />
		<AppNotification :title="getString('error')" :body-text="getString('share_link_error')" :type="-2" ref="error-share" />
		<AppNotification :title="getString('success')" :body-text="getString('referral_invite_success')" :type="1" ref="success-invite" :duration="6000" />
		<AppNotification :title="getString('error')" :body-text="getString('referral_invite_error')" :type="-2" ref="fail-invite" />
		<AppNotification :title="getString('warning')" :body-text="getString('referral_invite_already')" :type="-1" ref="already-invited" />
		<AppNotification :title="getString('error')" :body-text="getString('referral_invite_missing_partner')" :type="-2" ref="missing-partner-code" />
		<AppNotification :title="getString('error')" :body-text="getString('insert_valid_email')" :type="-2" ref="invalid-email" />
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import ReferredUsersTable from "@/user/components/settings/ReferredUsersTable.vue";
import RewardsManager from "@/user/components/settings/RewardsManager.vue";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
import {getPriceFormatted, validateEmail} from "@/shared/helpers/strings";
import AppNotification from "@/shared/components/AppNotification.vue";
import API from '@/shared/helpers/API.js';
import AppPopup from "@/shared/components/AppPopup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, lang: languageStore.lang, svgStore, getPriceFormatted, userStore}
	},
	name: "UserReferralContainer",
	components: {AppTextInput, AppPopup, AppNotification, AppCard, AppList, AppButton, RewardsManager, ReferredUsersTable},
	data() {
		return {
			referredBottomSheetActive: false,
			rewardsManagerActive: false,
			loadingSendEmail: false,
			referralLinkToShare: null,
			loadingShareLink: false,
			errorShareLink: false,
			invitingEmail: null,
			sharedFirstTime: false,
		}
	},
	computed: {
		instructionsListItems() {
			return [
				{customSlot: "instruction", icon: 'link', title: this.getString('referral_title_1'), subtitle: this.getString('referral_text_1')},
				{customSlot: "instruction", icon: 'iconPayments', title: this.getString('referral_title_2'), subtitle: this.getString('referral_text_2')},
				{customSlot: "instruction", icon: 'iconPresent', title: this.getString('referral_title_3'), subtitle: this.getString('referral_text_3')},
			]
		},
		popupButtons() {
			return [
				{text: this.getString('cancel'), callback: null, negative: true, loading: this.loadingSendEmail},
				{text: this.getString('ok'), callback: this.confirmEmailReferralInvite, loading: this.loadingSendEmail},
			]
		}
	},
	methods: {
		shareGeneralLink() {
			if(!this.referralLinkToShare) { return; }
			if(navigator && navigator.share && !this.sharedFirstTime) {
				this.sharedFirstTime = true;
				navigator.share({
					title: this.getString('share_referral_title'),
					text: this.getString('share_referral_text'),
					url: this.referralLinkToShare,
				}).then(() => {
					console.log("Shared successfully");
				}).catch(() => {
					console.warn("Error in sharing");
				});
			} else if(navigator && navigator.clipboard) {
				navigator.clipboard.writeText(this.referralLinkToShare).then(() => {
					this.$refs['success-share-copy'].openNotification();
					this.sharedFirstTime = true;
				}).catch(() => {
					this.$refs['error-share'].openNotification();
				});
			} else {
				this.errorShareLink = true;
			}
		},
		clickShareReferral() {
			if(this.loadingShareLink) { return }
			this.loadingShareLink = true;
			if(this.referralLinkToShare) {
				this.loadingShareLink = false;
				this.shareGeneralLink();
				return;
			}
			API.init().getReferralGenericUrl(resp=>{
				this.loadingShareLink = false;
				if(resp && resp.result === 'OK' && resp.referral_link) {
					this.referralLinkToShare = resp.referral_link;
					this.shareGeneralLink();
				} else {
					this.$refs[resp && resp.details === 'no_partner_code' ? 'missing-partner-code' : 'error-share'].openNotification();
				}
			});
		},
		clickEmailReferralInvite() {
			this.$refs["confirm-send-email"].toggleStatus(true);
			setTimeout(() => {
				this.$refs["inviting-email-input"].focus();
			}, 250);
		},
		confirmEmailReferralInvite() {
			if(document && document.activeElement && document.activeElement.blur) {
				document.activeElement.blur();
			}
			if(this.loadingSendEmail) { return }
			if(!validateEmail(this.invitingEmail)) {
				this.$refs['invalid-email'].openNotification();
				return;
			}
			this.loadingSendEmail = true;
			API.init().emailReferralInvite(this.invitingEmail, this.lang, resp=>{
				this.loadingSendEmail = false;
				let notificationRef = 'fail-invite';
				if(resp && resp.result === 'OK') {
					notificationRef = 'success-invite';
					this.invitingEmail = null;
					this.$refs["inviting-email-input"].currentValue = null;
					this.$refs['confirm-send-email'].toggleStatus(false);
				}
				notificationRef = resp && resp.details === 'wait_for_email' ? 'already-invited' : notificationRef;
				notificationRef = resp && resp.details === 'no_partner_code' ? 'missing-partner-code' : notificationRef;
				this.$refs[notificationRef].openNotification();
			});
		}
	}
}
</script>

<style scoped lang="scss">
.referral-container {

	:deep(.card-container) {
		background-color: var(--color-bg-primary-1);
	}

	.title-container {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		margin-bottom: 10px;

		:deep(svg) {
			.stroke { stroke: var(--txt-0);	}
		}

		p {
			font-size: 20px;
			margin-left: 10px;
			font-weight: 600;
		}
	}

	.second-title {
		font-weight: 500;
		font-size: 18px;
	}

	.card-background {
		background-color: var(--color-bg);
		background-image: url(https://img.qromo.io/img/referral-card-background-min.png);
		background-repeat: no-repeat;
		background-size: cover;
		box-shadow: 1px 3px 4px 0px var(--color-box-shadow-3) inset;
		border-radius: 26px;
	}

	.instructions-list {
		background-color: var(--color-alpha-1);
		border-radius: 26px;
		padding: 10px;

		:deep(.list-item) {
			padding-left: 0;

			&:after {
				display: none;
			}
		}

		.list-item-header {
			font-size: 16px;
			font-weight: 700;
		}
		.extra-text {
			font-size: 14px;
			color: var(--color-txt-1);
		}

		.icon-container {
			@include centered();
			width: 50px;
			:deep(svg) {
				width: 20px;
				height: 20px;
				.fill { fill: var(--txt-0);	}
				.stroke { stroke: var(--txt-0);	}
			}
		}

		.text-container {
			width: calc(100% - 50px);
		}
	}

	.referral-earnings-caption {
		font-size: 16px;
		color: var(--color-txt-2);
		margin-bottom: 6px;
	}
	.referral-earnings-value {
		color: var(--color-txt);
		font-size: 32px;
		font-weight: 500;
	}

	.referral-link-label {
		font-size: 12px;
		line-height: 12px;
		color: var(--color-txt-2);
	}
	.referral-link-text {
		font-size: 14px;
	}
}

</style>